import type { Dayjs } from 'dayjs';
import PeriodModel from '@/models/entities/PeriodModel';
import TeamMemberModel from '@/models/entities/TeamMemberModel';
import TimeEntryModel from '@/models/entities/TimeEntryModel';
import TimeSheetModel from '@/models/entities/TimeSheetModel';
import { listPage } from '@/models/listPage';
import { TEAM_MEMBER_STATUS, TIME_SHEET_STATUS } from '@/models/references/references';
import TimeSheetStatusHistoryModel from '@/models/services/TimeSheetStatusHistoryModel';
import { loadReferences, resolveReference } from '@/stores/useReferenceOptions';

/** Lignes d'une feuille chargées en une requête (maximum de l'API) : un mois en compte bien moins */
const MAX_ENTRIES = 300;

/** Un seul résultat, sans comptage */
const first = { page: 1, itemsPerPage: 1, partial: true } as const;

export interface TimesheetData {
  /** Dans une équipe active de la société : sinon, pas de déclaration possible (l'API refuse la soumission) */
  inTeam: boolean;
  /** Période ouverte de la société (une seule à la fois), null si aucune */
  period: PeriodModel | null;
  /** Feuille de l'employé pour cette période, null tant qu'il n'a rien enregistré */
  sheet: TimeSheetModel | null;
  /** Ses lignes, par date puis heure de début */
  entries: TimeEntryModel[];
  /** Date de soumission (dernière ligne Soumise de l'historique ; absence : sa déclaration), null pour un brouillon */
  submittedAt: Dayjs | null;
  /** Auteur de cette soumission (URN) : l'employé, ou le manager / Dirigeant qui a déclaré l'absence */
  submittedBy: string | null;
  /** Brouillon renvoyé par un refus : la ligne Refusée (date, motif) à montrer à l'employé, null sinon */
  rejection: TimeSheetStatusHistoryModel | null;
}

/** Clé de chargement (useLoader) : la feuille dépend de la société et du rattachement de l'employé */
export const timesheetKey = (companyId: number, memberId: number) => `${companyId}:${memberId}`;

/**
 * Feuille d'heure de l'employé dans une société, sur la période ouverte. La plus récente période de la société est la
 * seule ouverte (la clôture ouvre le mois suivant) : une requête triée par mois suffit, sans la liste des statuts.
 */
export async function loadTimesheet(key: string): Promise<TimesheetData> {
  const [companyId, memberId] = key.split(':').map(Number);
  const activeId = (await resolveReference('teamMemberStatuses', TEAM_MEMBER_STATUS.ACTIVE)).id;
  const [, teamMembers, periods] = await Promise.all([
    // Statut de la période (ouverte ?), statut et type (absence ?) de la feuille, lus par leur id
    loadReferences('periodStatuses', 'timeSheetStatuses', 'timeSheetTypes'),
    // Dans une équipe ? (rien d'autre à charger)
    listPage(TeamMemberModel, { filters: { member: memberId, teamMemberStatus: activeId }, ...first }, []),
    listPage(PeriodModel, { filters: { company: companyId }, order: { month: 'desc' }, ...first }),
  ]);
  const inTeam = teamMembers.models.length > 0;
  const latest = periods.models[0];
  const period = latest && !latest.isClosed ? latest : null;
  const empty = {
    inTeam,
    period,
    sheet: null,
    entries: [],
    submittedAt: null,
    submittedBy: null,
    rejection: null,
  };
  if (!period) return empty;

  // Son équipe (celle de son appartenance, figée à la soumission) ; la période est déjà là
  const sheets = await listPage(TimeSheetModel, { filters: { member: memberId, period: period.id }, ...first }, [
    ['teamMember', 'team'],
  ]);
  const sheet = sheets.models[0];
  if (!sheet) return empty;

  const submittedStatus = await resolveReference('timeSheetStatuses', TIME_SHEET_STATUS.SUBMITTED);
  const [entries, latestLines, submission] = await Promise.all([
    listPage(TimeEntryModel, {
      filters: { timeSheet: sheet.id },
      order: { startAt: 'asc' },
      page: 1,
      itemsPerPage: MAX_ENTRIES,
      partial: true,
    }),
    // Le refus : les deux dernières lignes (Refusée, puis le retour automatique en brouillon)
    listPage(TimeSheetStatusHistoryModel, {
      filters: { timeSheet: sheet.id },
      order: { id: 'desc' },
      page: 1,
      itemsPerPage: 2,
      partial: true,
    }),
    // La dernière soumission (par qui) ; l'historique complet se charge dans son bloc, paginé
    listPage(TimeSheetStatusHistoryModel, {
      filters: { timeSheet: sheet.id, timeSheetStatus: submittedStatus.id },
      order: { id: 'desc' },
      page: 1,
      itemsPerPage: 1,
      partial: true,
    }),
  ]);
  const lines = latestLines.models;
  const submitted = submission.models[0];
  // Refus : la ligne Refusée suivie du retour automatique en brouillon (les deux dernières de l'historique)
  const rejected = sheet.isDraft && lines[1]?.timeSheetStatusReference === TIME_SHEET_STATUS.REJECTED ? lines[1] : null;

  return {
    ...empty,
    sheet,
    entries: entries.models,
    submittedAt: sheet.isDraft ? null : (submitted?.createdAt ?? null),
    submittedBy: sheet.isDraft ? null : (submitted?.createdBy ?? null),
    rejection: rejected,
  };
}
